import { useEffect, useMemo, useRef, useState } from 'react';
import * as R from 'remeda';
import { useIsMutating } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { ApplyLabelPayload, Label } from '@rossum/api-client/labels';
import SearchInput from '@rossum/components/search-input/SearchInput';
import {
  CircularProgress,
  Dialog,
  MenuItem,
  MenuList,
  Stack,
} from '@rossum/ui/material';
import { throwError } from '../../redux/modules/messages/actions';
import { MENU_MAX_HEIGHT } from './components/constants';
import { EmptyListLabels } from './components/EmptyListLabels';
import { CreateLabelDialog } from './components/LabelDialogs';
import { LabelMenuActions } from './components/LabelMenuActions';
import { LabelMenuItem } from './components/LabelMenuItem';
import { Loading } from './components/Loading';
import { NoSearchResults } from './components/NoSearchResults';
import { useRequestUnpaginatedLabels } from './hooks/useRequestLabels';
import { MUTATION_KEY_LABELS_APPLY } from './hooks/useRequestLabelsApply';

type OnLabelArgument = {
  payload: ApplyLabelPayload;
  onSuccess?: () => void;
  onError?: () => void;
};

export type OnLabelAction = (args: OnLabelArgument) => void;

type AnnotationsLabelsListProps = {
  annotations: { labels: Array<string>; url: string }[];
  onLabelsChanged: OnLabelAction;
  onClose: () => void;
  isClosing?: boolean;
};

const defaultOperations: Required<ApplyLabelPayload['operations']> = {
  add: [],
  remove: [],
};

export const AnnotationsLabelsList = ({
  annotations,
  onLabelsChanged,
  onClose,
  isClosing = false,
}: AnnotationsLabelsListProps) => {
  const intl = useIntl();
  const menuListRef = useRef<HTMLUListElement>(null);
  const [createdLabel, setCreatedLabel] = useState<Label | null>(null);

  const [operations, setOperations] =
    useState<Required<ApplyLabelPayload['operations']>>(defaultOperations);

  const changedLabels = [...operations.add, ...operations.remove];

  const initialState = useMemo(() => {
    const annotationsLabels = annotations.map(({ labels }) => labels);

    const allChecked = annotationsLabels.reduce(
      (acc, labels) => R.intersection(acc ?? [], labels),
      annotationsLabels.length > 0 ? annotationsLabels[0] : []
    );

    const indeterminates = annotationsLabels
      .flat()
      .filter(label => !allChecked?.includes(label));

    return {
      indeterminates,
      allChecked,
    };
  }, [annotations]);

  const labelsAreApplying = !!useIsMutating({
    mutationKey: [MUTATION_KEY_LABELS_APPLY],
  });

  const [searchTerm, setSearchTerm] = useState('');

  const { data: allLabels, isInitialLoading: isLoading } =
    useRequestUnpaginatedLabels();

  const filteredLabels = useMemo(() => {
    if (!allLabels || !searchTerm) return allLabels;

    return allLabels.filter(label =>
      label.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allLabels, searchTerm]);

  // Check if we have any labels at all
  const hasLabels = (allLabels?.length ?? 0) > 0;
  // Show "no search results" only when we have labels but the search filtered them all out
  const hasNoSearchResults = hasLabels && filteredLabels?.length === 0;

  const dispatch = useDispatch();
  const applyLabels = () => {
    const annotationUrls = annotations.map(({ url }) => url);
    const payload = {
      operations,
      objects: { annotations: annotationUrls },
    };

    onLabelsChanged({
      payload,
      onSuccess: () => {
        setOperations(defaultOperations);
        onClose();
      },
      onError: () => {
        dispatch(throwError('labelApplyError'));
      },
    });
  };

  const [showCreateLabelDialog, setShowCreateLabelDialog] =
    useState<boolean>(false);

  useEffect(() => {
    if (createdLabel && menuListRef.current && filteredLabels) {
      // Using data.length, and not data.length -1, ensures that we scroll just at the top edge
      // of the createdLabel.
      const relativePosition =
        filteredLabels.findIndex(label => label.id === createdLabel.id) /
        filteredLabels.length;

      // see https://github.com/facebook/react/issues/23396 for why we don't use scrollIntoView
      menuListRef.current.scrollTo({
        top: menuListRef.current.scrollHeight * relativePosition,
        behavior: 'smooth',
      });
    }
  }, [createdLabel, filteredLabels]);

  return (
    <>
      {isLoading ? (
        <Loading />
      ) : (
        <Stack minHeight="inherit">
          <Stack sx={{ p: 2, pb: 1 }}>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder={intl.formatMessage({
                id: 'components.labels.labelsFiltering.searchPlaceholder',
              })}
            />
          </Stack>
          {hasNoSearchResults ? (
            <NoSearchResults />
          ) : filteredLabels?.length ? (
            <>
              <MenuList
                ref={menuListRef}
                sx={{
                  maxHeight: MENU_MAX_HEIGHT - 100,
                  overflowY: 'auto',
                  overflowX: 'hidden',
                }}
              >
                {filteredLabels.map(label => {
                  const isChanged = changedLabels.includes(label.url);
                  const checkedProps = isChanged
                    ? {
                        checked: operations.add.includes(label.url),
                        indeterminate: undefined,
                      }
                    : {
                        checked: !!initialState.allChecked?.includes(label.url),
                        indeterminate: initialState.indeterminates.includes(
                          label.url
                        ),
                      };

                  const shouldAppend =
                    (isChanged &&
                      initialState.indeterminates.includes(label.url)) ||
                    initialState.allChecked?.includes(label.url);

                  const handleChange = () => {
                    if (checkedProps.checked) {
                      return setOperations(prev => ({
                        add: prev.add.filter(item => item !== label.url),
                        remove: shouldAppend
                          ? [...prev.remove, label.url]
                          : prev.remove,
                      }));
                    }
                    return setOperations(prev => ({
                      add: shouldAppend ? prev.add : [...prev.add, label.url],
                      remove: prev.remove.filter(item => item !== label.url),
                    }));
                  };

                  return (
                    <MenuItem key={label.id} dense onClick={handleChange}>
                      <LabelMenuItem label={label} {...checkedProps} />
                    </MenuItem>
                  );
                })}
              </MenuList>

              <LabelMenuActions
                shouldShowApplyButton={isClosing || !!changedLabels.length}
                applyButtonProps={{
                  endIcon: labelsAreApplying && (
                    <CircularProgress size={18} color="inherit" />
                  ),
                  onClick: applyLabels,
                  disabled: changedLabels.length === 0,
                }}
                onCreateLabel={() => setShowCreateLabelDialog(true)}
              />
            </>
          ) : (
            <EmptyListLabels>
              <LabelMenuActions
                shouldShowApplyButton={false}
                onCreateLabel={() => setShowCreateLabelDialog(true)}
              />
            </EmptyListLabels>
          )}
        </Stack>
      )}
      <Dialog open={showCreateLabelDialog} PaperProps={{ elevation: 2 }}>
        <CreateLabelDialog
          onClose={() => setShowCreateLabelDialog(false)}
          onSuccess={newLabel => {
            setOperations(prev => ({
              ...prev,
              add: [...prev.add, newLabel.url],
            }));
            setCreatedLabel(newLabel);
          }}
        />
      </Dialog>
    </>
  );
};
